import type { Group } from '../groups/groupsApi.ts';
import { ListSettings } from '../items/ListSettings.tsx';
import { ListView } from '../items/ListView.tsx';
import { NewListForm } from '../items/NewListForm.tsx';
import { useLists } from '../items/useLists.ts';
import { useSelectedList } from '../items/useSelectedList.ts';
import { Modal } from '../../ui/Modal.tsx';

interface GroupViewProps {
  group: Group;
  /** Driven by the menu in the header — see HomePage. */
  isListsOpen: boolean;
  onCloseLists: () => void;
}

/**
 * Everything scoped to one group.
 *
 * Mounted with a key of the group id, so switching household throws all of this away
 * and starts again — the chosen list, any open editor, any half-typed form. That is a
 * remount rather than a pile of synchronising effects, and it is why none of the state
 * below needs to know that groups can change.
 *
 * List selection lives here rather than in ListView because the lists panel needs to act
 * on the same list the switcher is showing. It is also the reason that panel is rendered
 * here and the group one is not: this is the only piece of menu state with a dependency
 * further down the tree.
 */
export function GroupView({ group, isListsOpen, onCloseLists }: GroupViewProps) {
  const { data: lists } = useLists(group.id);

  const { selected: list, select: selectList } = useSelectedList(group.id, lists);

  return (
    <>
      {/* The colour key used to be drawn here, between the group name and the list. It
          belongs to the list screen — it explains the stripes on the rows — and sitting
          above ListView pushed the list switcher a whole block away from the group name
          it names a part of. ListView already has the members, so it draws it itself. */}
      <ListView group={group} lists={lists ?? []} list={list} onSelectList={selectList} />

      {/* Occasional admin, off the shopping screen entirely. It used to be a disclosure
          below the list, which meant every one of these forms was one stray tap away from
          the thing people actually came to do — and the list moved under their thumb when
          it opened. Nothing in here is needed while shopping. */}
      {isListsOpen && (
        <Modal title="Lists" onClose={onCloseLists}>
          <div className="settings">
            {list && (
              <ListSettings groupId={group.id} list={list} listCount={lists?.length ?? 1} />
            )}

            <NewListForm groupId={group.id} />
          </div>
        </Modal>
      )}
    </>
  );
}
